import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useRef } from "react";

// third-party
import { Chance } from "chance";
import jwtDecode from "jwt-decode";

// reducer - state management
import accountReducer from "store/accountReducer";
import { LOGIN, LOGOUT } from "store/actions";

// project imports
import Axios from "axios";
import { API_URL } from "config";
import Loader from "ui-component/Loader";
import axios from "utils/axios";

const SERVER_API_URL = API_URL;
const chance = new Chance();

// Logout at 12:00 AM in this timezone (IANA name, e.g. "Asia/Kolkata")
const LOGOUT_MIDNIGHT_TIMEZONE =
  process.env.REACT_APP_LOGOUT_TIMEZONE || "Asia/Kolkata";
// ISO offset for that timezone (e.g. "+05:30" for IST, "-05:00" for EST)
const LOGOUT_MIDNIGHT_OFFSET =
  process.env.REACT_APP_LOGOUT_MIDNIGHT_OFFSET || "+05:30";

/**
 * Returns milliseconds until next 12:00 AM (midnight) in the configured timezone.
 * All users are logged out at the same clock time in that timezone.
 */
function getMsUntilNextMidnightInTimezone() {
  const now = new Date();
  const todayStr = now.toLocaleDateString("en-CA", {
    timeZone: LOGOUT_MIDNIGHT_TIMEZONE,
  }); // "YYYY-MM-DD"
  const [y, m, d] = todayStr.split("-").map(Number);
  const tomorrowUTC = new Date(Date.UTC(y, m - 1, d + 1));
  const y2 = tomorrowUTC.getUTCFullYear();
  const m2 = tomorrowUTC.getUTCMonth() + 1;
  const d2 = tomorrowUTC.getUTCDate();
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  const nextMidnightInTZ = new Date(
    `${y2}-${pad(m2)}-${pad(d2)}T00:00:00${LOGOUT_MIDNIGHT_OFFSET}`,
  );
  return Math.max(0, nextMidnightInTZ.getTime() - now.getTime());
}

const FIXED_LOGOUT_MS = 2 * 60 * 1000; // 2 minutes
// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }

  const decoded = jwtDecode(serviceToken);

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem("serviceToken", serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem("serviceToken");
    localStorage.removeItem("users");
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem("serviceToken");
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const response = await axios.get("/api/account/me");
          const { user } = response.data;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user,
            },
          });
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT,
        });
      }
    };

    init();
  }, []);

  // Auto logout at 12:00 AM every night in configured timezone (e.g. Asia/Kolkata); no activity or API dependency
  const logoutTimerRef = useRef(null);
  useEffect(() => {
    if (!state.isLoggedIn) return;

    const performLogout = () => {
      setSession(null);
      dispatch({ type: LOGOUT });
      window.location.href = window.location.origin + "/login";
    };

    const msUntilMidnight = getMsUntilNextMidnightInTimezone();
    logoutTimerRef.current = setTimeout(performLogout, msUntilMidnight);
    // logoutTimerRef.current = setTimeout(performLogout, FIXED_LOGOUT_MS);
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [state.isLoggedIn, dispatch]);

  const login = async (email, password, RememberMe) => {
    if (RememberMe === true) {
      localStorage.setItem("email", email);
      localStorage.setItem("password", password);
    }
    if (RememberMe === false) {
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }
    const response = await Axios.post(SERVER_API_URL + "/api/auth/signin", {
      email,
      password,
    });

    if (response.data.status !== "200") {
      return response.data;
    } else if (response.data.status === "200") {
      const serviceToken = response.data.data.accesstoken;

      setSession(serviceToken);
      let user = {
        id: response.data.data.id,
        email: response.data.data.email,
        name: response.data.data.email,
        groupId: response.data.data.groupId,
        role_id: response.data.data.role_id,
        cn_role_id: response.data.data.cn_role_id,
      };
      window.localStorage.setItem("users", JSON.stringify(user));
      window.location.href = "/CommunityDashboard";
      /* dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
      },
      }); */

      return response.data;
    }
  };

  const googleLogin = async (serviceToken) => {
    try {
      setSession(serviceToken);

      const decoded = jwtDecode(serviceToken);

      const user = {
        id: decoded.id,
      };

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user,
        },
      });

      return true;
    } catch (error) {
      console.error("Google login failed:", error);
      dispatch({ type: LOGOUT });
      return false;
    }
  };

  const delay = (timeout) => new Promise((res) => setTimeout(res, timeout));

  const register = async (email, password, firstName, lastName, mobile) => {
    try {
      await delay(500);

      if (!email || !password) {
        return [400, { message: "Enter Your Email & Password" }];
      }

      if (!firstName || !lastName) {
        return [400, { message: "Enter Your Name" }];
      }

      const id = chance.bb_pin();

      const response = await Axios.post(SERVER_API_URL + "/api/auth/signup", {
        id,
        email,
        password,
        firstName,
        lastName,
        mobile,
      });

      return response.data;
    } catch (err) {
      return [500, { message: "Server Error" }];
    }
  };

  const registerOtpVerify = async (regUserId, otp) => {
    const id = chance.bb_pin();
    const response = await axios.post("/api/account/registerotpverify", {
      id,
      regUserId,
      otp,
    });

    return response.data;
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email) => {
    try {
      const ServerResponseNext = await Axios.post(
        SERVER_API_URL + "/api/account/forgot",
        { email },
      );

      let userRes = [];
      if (
        ServerResponseNext.data.status &&
        ServerResponseNext.data.status === "200"
      ) {
        userRes = [200, ServerResponseNext.data.data];
      } else {
        userRes = [201, ServerResponseNext.data];
      }
      return userRes;
    } catch (err) {
      console.error(err);
      return [500, { message: "Server Error" }];
    }
  };

  const verifyotp = async (UserId, otp) => {
    try {
      const ServerResponseNext = await Axios.post(
        SERVER_API_URL + "/api/account/forgot/verifyotp",
        { UserId, otp },
      );

      let userRes = [];
      if (
        ServerResponseNext.data.status &&
        ServerResponseNext.data.status === "200"
      ) {
        userRes = [200, ServerResponseNext.data];
      } else {
        userRes = [201, ServerResponseNext.data];
      }
      return userRes;
    } catch (err) {
      console.error(err);
      return [500, { message: "Server Error" }];
    }
  };

  const newPassword = async (UserId, Password) => {
    try {
      const ServerResponseNext = await Axios.post(
        SERVER_API_URL + "/api/auth/reset_password",
        { UserId, Password },
      );

      let userRes = [];
      if (
        ServerResponseNext.data.status &&
        ServerResponseNext.data.status === "200"
      ) {
        userRes = [200, ServerResponseNext.data];
      } else {
        userRes = [201, ServerResponseNext.data];
      }
      return userRes;
    } catch (err) {
      console.error(err);
      return [500, { message: "Server Error" }];
    }
  };
  const Profiledata = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/profile_view", { userId });
    verifyAuthorization(response);
    return response.data;
  };

  const SociallinkView = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Sociallink_view", { userId });
    verifyAuthorization(response);
    return response;
  };
  const updateProfile = async (
    fname,
    lname,
    city,
    tradingviewIid,
    address,
    occupation,
    userId,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/profile_update", {
      fname,
      lname,
      city,
      tradingviewIid,
      address,
      occupation,
      userId,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const updateSociallink = async (
    about,
    instaLink,
    fbLink,
    twitterLink,
    linkedinLink,
    telegramLink,
    userId,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Sociallinkupdate", {
      about,
      instaLink,
      fbLink,
      twitterLink,
      linkedinLink,
      telegramLink,
      userId,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const AllUsers = async (
    userId,
    search,
    GroupFilterstate,
    searchfilterstate,
    page,
    pageSize,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/AllUsers", {
      userId,
      search,
      GroupFilterstate,
      searchfilterstate,
      page,
      pageSize,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const Addfriend = async (userId, followUserId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/follow_member", {
      userId,
      followUserId,
    });
    verifyAuthorization(response);
    return response.data;
  };
  const Removefriend = async (userId, followUserId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/unfollow_member", {
      userId,
      followUserId,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const MyFollower = async (userId, search, page, pageSize, followUserId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/followers_list", {
      userId,
      search,
      page,
      pageSize,
      followUserId,
    });
    verifyAuthorization(response);
    return response;
  };

  const MyFriends = async (userId, search, page, pageSize) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/following_list", {
      userId,
      search,
      page,
      pageSize,
    });
    verifyAuthorization(response);
    return response;
  };

  const MyGrouplist = async (userId, groupId, search, page, pageSize) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/mygroup_members_list", {
      userId,
      groupId,
      search,
      page,
      pageSize,
    });
    verifyAuthorization(response);
    return response;
  };

  const Statistics = async (userId, memberId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/members_statistics", {
      userId,
      memberId,
    });
    verifyAuthorization(response);
    return response;
  };
  const MemberProfile = async (userId, memberId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/view_member_profile", {
      userId,
      memberId,
    });
    verifyAuthorization(response);
    return response;
  };

  const Qscore = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Qscore", {
      userId,
    });
    verifyAuthorization(response);
    return response.data;
  };
  const Rewards_chart = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/rewardschart", { userId });
    verifyAuthorization(response);
    return response.data;
  };

  const watchlist = async (userId, watchType) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/watch_list", {
      userId,
      watchType,
    });
    verifyAuthorization(response);
    return response;
  };

  const corewatchlist = async (userId, watchType) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/getcompanylistCore",
      { userId, watchType },
    );
    return response;
  };
  const coreInsertwatchlist = async (
    companyId,
    userId,
    watchtype,
    currentDate,
    currentyear,
    role_id,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/coreInsertwatchlist",
      {
        companyId,
        userId,
        watchtype,
        currentDate,
        currentyear,
        role_id,
      },
    );

    return response;
  };
  const corewatchlisthistory = async (
    userId,
    weeknum,
    CurrentYear,
    searchTerm,
    startDate,
    endDate,
    selectedFilter,
    selectedFilterMonth,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/Getdata ",
      {
        userId,
        weeknum,
        CurrentYear,
        searchTerm,
        startDate,
        endDate,
        selectedFilter,
        selectedFilterMonth,
      },
    );

    return response;
  };

  const DeleteCoreWatchlist = async (userId, wid, companyid) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/deletecorewatchlisthistory",
      {
        userId,
        wid,
        companyid,
      },
    );
    return response;
  };

  const EditCoreWatchlistgetdata = async (userId, watchId) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/EditGetdata",
      {
        userId,
        watchId,
      },
    );
    return response;
  };

  // const Insertwatchlist = async (
  //   companyId,
  //   userId,
  //   watchtype,
  //   currentDate,
  //   currentyear
  // ) => {
  //   const serviceToken = window.localStorage.getItem("serviceToken");
  //   axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  //   const response = await Axios.post(
  //     SERVER_API_URL + "/api/watchlist/addwatchlist",
  //     {
  //       companyId,
  //       userId,
  //       watchtype,
  //       currentDate,
  //       currentyear,
  //     }
  //   );
  //   verifyAuthorization(response);
  //   return response;
  // };

  const Insertwatchlist = async (
    companyId,
    fincode,
    userId,
    watchtype,
    currentDate,
    currentyear,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/addwatchlist",
      {
        companyId,
        fincode,
        userId,
        watchtype,
        currentDate,
        currentyear,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const weekly = async (userId, watchType, weeknum) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/weekly", {
      userId,
      watchType,
      weeknum,
    });
    // const { serviceToken, user } = response.data;
    verifyAuthorization(response);
    return response;
  };

  const weeklist = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/weeklist");
    // const { serviceToken, user } = response.data;
    verifyAuthorization(response);
    return response;
  };

  const DeleteWatchlist = async (userId, wid, companyid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/deletewatchlisthistory",
      {
        userId,
        wid,
        companyid,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const Resourceslist = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Resources");
    verifyAuthorization(response);
    return response;
  };
  const ResearchList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Research");
    verifyAuthorization(response);
    return response;
  };

  const TemplateList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Templates");
    verifyAuthorization(response);
    return response;
  };

  const TechnoList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/Techno");
    verifyAuthorization(response);
    return response;
  };
  const ApplicationsList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/apps");
    verifyAuthorization(response);
    return response;
  };

  const ResourceCategoryList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/resource/categories",
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminGetCategories = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/resource/admin/categories",
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminAddCategory = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/category/add",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminUpdateCategory = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/category/update",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminDeleteCategory = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/category/delete",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminBulkDeleteCategories = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/category/bulk-delete",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminGetResources = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/resource/admin/resources",
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminAddResource = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/resource/add",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminUpdateResource = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/resource/update",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminDeleteResource = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/resource/delete",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminBulkDeleteResources = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/resource/bulk-delete",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  const RefAdminUploadResourceImage = async (file) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    const fd = new FormData();
    fd.append("image", file);
    const response = await Axios.post(
      SERVER_API_URL + "/api/resource/admin/upload-image",
      fd,
      {
        headers: {
          Authorization: `Bearer ${serviceToken}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const GetLearing = async (userId, topicType) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/user/GetLearing", {
      userId,
      topicType,
    });
    verifyAuthorization(response);
    return response;
  };

  const DeleteLearningHistory = async (userId, topicid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/LearnPath/DeleteLearningHistory",
      { userId, topicid },
    );
    verifyAuthorization(response);
    return response;
  };

  const InsertLearningHistory = async (userId, topicid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/InsertLearningHistory",
      { userId, topicid },
    );
    verifyAuthorization(response);
    return response;
  };

  const CheckTotalCountOfLearning = async (userId, topicType) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/LearnPath/CheckTotalCountOfLearning",
      { userId, topicType },
    );
    verifyAuthorization(response);
    return response;
  };

  const CheckStatusOfLearning = async (userId, topicType) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/CheckStatusOfLearning",
      { userId, topicType },
    );
    verifyAuthorization(response);
    return response;
  };

  const Alloccupationlist = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/Profile/Alloccupationlist");
    verifyAuthorization(response);
    return response;
  };

  const ProcessUpdate = async (profileProcess, userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/Profile/processUpdate", {
      profileProcess,
      userId,
    });
    verifyAuthorization(response);
    return response.data;
  };
  const watchlistdata = async (userId, weeknum, CurrentYear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/getwatchlisthistory",
      {
        userId,
        weeknum,
        CurrentYear,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const vivekwatchlistdata = async (userId, weeknum, CurrentYear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/getvivekwatchlisthistory",
      {
        userId,
        weeknum,
        CurrentYear,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const CoreauditCashdatalist = async (userId, quarterly, CurrentYear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/getcoreauditCash",
      { userId, quarterly, CurrentYear },
    );
    verifyAuthorization(response);
    return response;
  };

  const UpdateCoreauditCashdata = async (
    userId,
    quarterly,
    curyear,
    onInvesting,
    onCurrent,
    returnVal,
    comment,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/updatecoreauditCash",
      {
        userId,
        quarterly,
        curyear,
        onInvesting,
        onCurrent,
        returnVal,
        comment,
      },
    );

    verifyAuthorization(response);
    return response;
  };

  const Coreauditcompanylist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/getcoreauditcompanylist",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const Coreauditdatalist = async (userId, userIds) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/getcoreaudit",
      { userId, userIds },
    );
    verifyAuthorization(response);
    return response;
  };

  const updateCoreAudit = async (
    onInvesting,
    onCurrent,
    returnVal,
    comment,
    HP,
    userId,
    companyId,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/coreauditupdate",
      {
        onInvesting,
        onCurrent,
        returnVal,
        comment,
        HP,
        userId,
        companyId,
      },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const insertCoreAudit = async (userId, companyId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/coreauditinsert",
      { userId, companyId },
    );
    verifyAuthorization(response);
    return response.data;
  };
  const deleteCoreAudit = async (userId, companyId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/coreauditDelete",
      { userId, companyId },
    );
    verifyAuthorization(response);
    return response.data;
  };
  // Replaces the caller's audit with the source account's (Vivek, user 708 --
  // the id lives on the server). The server takes the target from the token, so
  // the userId sent here can only ever be the caller's own.
  const copyVivekAudit = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/copyVivekAudit",
      { userId },
    );
    verifyAuthorization(response);
    return response.data;
  };


  const checkanswerdata = async (User_Id, weekNumber) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/checkanswerdata",
      { User_Id, weekNumber },
    );
    verifyAuthorization(response);
    return response;
  };
  const charitylistdata = async (userid, userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/charity_listdata",
      { userid, userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const deletecharitydata = async (userId, nameCom, userid, month, amount) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/delete_charitylistdata",
      { userId, nameCom, userid, month, amount },
    );
    verifyAuthorization(response);
    return response;
  };
  const Insertcharitylist = async (
    charityid,
    userid,
    year,
    month,
    amount,
    addedon,
    monthid,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/insertcharity_list",
      { charityid, userid, year, month, amount, addedon, monthid },
    );
    verifyAuthorization(response);
    return response;
  };
  const charitylist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/charity_list",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const Editcharitydata = async (
    charityListid,
    year1,
    month,
    amount,
    charityList,
    userId,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/editcharitydata",
      { charityListid, year1, month, amount, charityList, userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const weekdata1 = async (charityid, userid, watchtype) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/user/weekdata1", {
      charityid,
      userid,
      watchtype,
    });
    // const { serviceToken, user } = response.data;
    verifyAuthorization(response);
    return response;
  };

  const Email = async (userId, userIds) => {
    try {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(SERVER_API_URL + "/api/user/Email", {
        userId,
        userIds,
      });
      verifyAuthorization(response);
      return response.data;
    } catch (err) {
      return [500, { message: "Server Error" }];
    }
  };

  const TopTwentyuserDetailsBaseOnRewaerdPoint = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/TopTwentyuserDetailsBaseOnRewaerdPoint",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const TrendingCorePortfolio = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/TrendingCorePortfolio",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const TrendingSatelliteWatchlist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/TrendingSatelliteWatchlist",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const SW_One_Percent = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/SW_One_Percent", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const sectorWatchlist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/sectorWatchlist", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

  // The Sector Insights page's one call. Named SectorInsightsData, not
  // SectorInsights: the page's component is `const SectorInsights`, and the
  // destructure would shadow it.
  const SectorInsightsData = async (userId, year, week) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/sectorInsights", {
      userId,
      year,
      week,
    });
    verifyAuthorization(response);
    return response;
  };

  const RewardsPoint = async (userId, userIds) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/RewardsPoint", {
      userId,
      userIds,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const Pastevent = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/event/Pastevent", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const Futurevent = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/Futurevent",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const Todayevent = async (userId, Eventuser_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/Todayevent",
      { userId, Eventuser_id },
    );
    verifyAuthorization(response);
    return response;
  };
  const GetAttendance = async (userId, groupId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/Attendance",
      { userId, groupId },
    );
    verifyAuthorization(response);
    return response;
  };
  const InsertAttendance = async (
    userId,
    scheduleId,
    eventId,
    attendanceClickedButton,
    event_type,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/InsertEventTracking",
      { userId, scheduleId, eventId, attendanceClickedButton, event_type },
    );
    verifyAuthorization(response);
    return response;
  };
  const AttendanceChartData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/AttendanceChartData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const updateVideoLink = async (
    userId,
    eventId,
    EditVideoLink,
    EditVideoLink2,
    EditVideoCompanyName1,
    EditVideoCompanyName2,
    EditVideoPassword1,
    EditVideoPassword2,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/updatevideolink",
      {
        userId,
        eventId,
        EditVideoLink,
        EditVideoLink2,
        EditVideoCompanyName1,
        EditVideoCompanyName2,
        EditVideoPassword1,
        EditVideoPassword2,
      },
    );
    verifyAuthorization(response);
    return response.data;
  };
  const CalendarEventsList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/CalendarEventsList",
      { userId },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const profilevisibilityInsert = async (userId, module) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/profile_visibility_insert",
      { userId, module },
    );
    verifyAuthorization(response);
    return response;
  };

  const profilevisibility = async (userId, userIds) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/profile_visibility",
      { userId, userIds },
    );
    verifyAuthorization(response);
    return response;
  };

  const UserDetails = async (
    userId,
    search,
    GroupFilterstate,
    currentPage,
    limit,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/userdetail",
      { userId, search, GroupFilterstate, currentPage, limit },
    );
    verifyAuthorization(response);
    return response;
  };

  const Editdatauser = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/Editdatauser",
      { userId, value },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditSavedata = async (
    userId,
    ValueCheck,
    emaildata,
    GroupIddata,
    StartDate,
    fnamedata,
    lastnamdata,
    Phonenumber,
    EndDate,
    Badgedata,
    Statusdata,
    Pandata,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/Editsavedata",
      {
        userId,
        ValueCheck,
        emaildata,
        GroupIddata,
        StartDate,
        fnamedata,
        lastnamdata,
        Phonenumber,
        EndDate,
        Badgedata,
        Statusdata,
        pandata: Pandata,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const Adddatauser = async (
    userId,
    Addfnamedata,
    Addlnamedata,
    Addemaildata,
    Addmobilenumberdata,
    AddGroupiddata,
    AddBadgedata,
    AddCitydata,
    Addaddressdata,
    Addaboutdata,
    AddSubscriptionstartdata,
    AddSubscriptionenddata,
    Addstatusdata,
    Addmembersincedata,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/Adddatauser",
      {
        userId,
        Addfnamedata,
        Addlnamedata,
        Addemaildata,
        Addmobilenumberdata,
        AddGroupiddata,
        AddBadgedata,
        AddCitydata,
        Addaddressdata,
        Addaboutdata,
        AddSubscriptionstartdata,
        AddSubscriptionenddata,
        Addstatusdata,
        Addmembersincedata,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  // The User Management "Update PAN" upload. The workbook goes up as multipart
  // and the server reads the Email and PAN columns; the browser never parses it.
  const PanXlsxUpdate = async (file) => {
    withServiceToken();
    const formData = new FormData();
    formData.append("file", file);
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/PanXlsxUpdate",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    verifyAuthorization(response);
    return response;
  };


  const getEc2TypeChangeToggle = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/ec2_type_change/status",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const updateEc2TypeChangeToggle = async (userId, isenabled) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/ec2_type_change/update",
      { userId, isenabled },
    );
    verifyAuthorization(response);
    return response;
  };

  const Csvreport = async (userId, module, date) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/Csvreport",
      { userId, module, date },
    );
    return response;
  };

  const CharityReport = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/CharityReport",
      { userId, value },
    );
    return response;
  };
  const Watchlist = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/SatelliteWatchListReport",
      { userId, value },
    );
    return response;
  };

  const LoginReport = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/LoginReport",
      { userId, value },
    );
    return response;
  };

  const NotloginReportdata = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/NotLoginReport",
      { userId, value },
    );
    return response;
  };
  const CoreAuditReportdata = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/CoreAuditReport",
      { userId, value },
    );
    return response;
  };

  const EventList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/event/EventList", {
      userId,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const GroupnameList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/GroupnameList",
      { userId },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const AddEvent = async (
    eventId,
    eventStartFormatted,
    eventEndFormatted,
    userTimeZone,
    eventStart,
    eventEnd,
    groupId,
    userId,
    considerAttendance,
    Description,
    EventName,
    Link,
    Recurrencevalue,
    occurrences,
    weekarr,
    eid,
    Event_Type,
    event_type_id,
    Presented_by_Id,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/event/AddEvent", {
      eventId,
      eventStartFormatted,
      eventEndFormatted,
      userTimeZone,
      eventStart: eventStartFormatted,
      eventEnd: eventEndFormatted,
      groupId,
      userId,
      considerAttendance,
      Description,
      EventName,
      Link,
      Recurrencevalue,
      occurrences,
      weekarr,
      eid,
      Event_Type,
      event_type_id,
      Presented_by_Id,
    });
    verifyAuthorization(response);
    return response.data;
  };
  const GetEventsList = async (
    userId,
    search,
    FilterGroup,
    timeline,
    page,
    pageSize,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/GetEventsList",
      { userId, search, FilterGroup, timeline, page, pageSize },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const DeleteSchedule = async (userId, Id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/DeleteSchedule",
      { userId, Id },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const DeleteAllSchedule = async (userId, EventId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/DeleteAllSchedule",
      { userId, EventId },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const updateEvent = async (
    eventId,
    eventEnd,
    Description,
    Link,
    userId,
    eventStart,
    groupId,
    EditEvent_consider_Attendance,
    EId,
    Name,
    event_type_id,
    EditEventType,
    Presented_by_Id,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/updateEvent",
      {
        eventId,
        eventEnd,
        Description,
        Link,
        userId,
        eventStart,
        groupId,
        EditEvent_consider_Attendance,
        EId,
        Name,
        event_type_id,
        EditEventType,
        Presented_by_Id,
      },
    );
    verifyAuthorization(response);
    return response.data;
  };
  //stock
  const stockInsert = async (
    userId,
    alert_name,
    companyId,
    stop_loss,
    profit_target,
    expiry_date,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/stockInsert",
      { userId, alert_name, companyId, stop_loss, profit_target, expiry_date },
    );
    verifyAuthorization(response);
    return response;
  };
  const stocklist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/stocklist",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const StockAlertList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/StockAlertList",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const stockalertUpdate = async (
    userId,
    alert_id,
    stop_loss,
    expiry_date,
    profit_target,
    alert_name,
    status,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/stockalertUpdate",
      {
        userId,
        alert_id,
        stop_loss,
        expiry_date,
        profit_target,
        alert_name,
        status,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const stockalertDelete = async (userId, alert_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/event/stockalertDelete",
      { userId, alert_id },
    );
    verifyAuthorization(response);
    return response;
  };
  const stockalertviewdata = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/stockChart", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

  const Getcategory = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/getcategorylist", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

const ContactInsert = async (
    userId,
    category,
    email,
    mobile,
    subject,
    content,
    files,
    ext,
  ) => {
    const data = new FormData();
    let response = "";
    let fileName = "";
    if (files.length !== 0) {
      data.append("file", files[0]);
      // Lower-case so a .PDF/.PNG the form already accepted still uploads.
      const ext = files[0].name
        .substring(files[0].name.lastIndexOf(".") + 1)
        .toLowerCase();

      if (ext === "pdf" || ext === "png" || ext === "jpg" || ext === "jpeg") {
        const upload = await Axios.post(
          SERVER_API_URL + "/uploadAttachment",
          data,
        ).then((upload) => {
          fileName = upload.data[0].filename;
          response = InsertWithLogin(
            userId,
            category,
            email,
            mobile,
            subject,
            content,
            fileName,
          );
        });
      }
      return response;
    } else {
      response = InsertWithLogin(
        userId,
        category,
        email,
        mobile,
        subject,
        content,
        fileName,
      );
      //  verifyAuthorization(response);
      return response;
    }
  };
  const GetcategoryWithoutLogin = async (userId) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/getcategorylistWithoutlogin",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const ContactInsertWithoutLogin = async (
    userId,
    category,
    email,
    mobile,
    subject,
    content,
    files,
  ) => {
    const data = new FormData();
    let response = "";
    let fileName = "";
    if (files.length !== 0) {
      data.append("file", files[0]);
      // Lower-case so a .PDF/.PNG the form already accepted still uploads.
      const ext = files[0].name
        .substring(files[0].name.lastIndexOf(".") + 1)
        .toLowerCase();
      if (ext === "pdf" || ext === "png" || ext === "jpg" || ext === "jpeg") {
        const upload = await Axios.post(
          SERVER_API_URL + "/uploadAttachment",
          data,
        ).then((upload) => {
          fileName = upload.data[0].filename;
          response = Insert(
            userId,
            category,
            email,
            mobile,
            subject,
            content,
            fileName,
          );
        });
      }
      return response;
    } else {
      response = Insert(
        userId,
        category,
        email,
        mobile,
        subject,
        content,
        fileName,
      );

      return response;
    }
  };

  const Insert = async (
    userId,
    category,
    email,
    mobile,
    subject,
    content,
    fileName,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/contactInsertWithoutlogin",
      { userId, category, email, mobile, subject, content, fileName },
    );

    return response;
  };

  const EventReport = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/EventlatestReport",
      { userId, value },
    );
    return response;
  };

  const AlertReport = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/AlertReport",
      { userId, value },
    );
    return response;
  };

  const InsertWithLogin = async (
    userId,
    category,
    email,
    mobile,
    subject,
    content,
    fileName,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/contactInsert", {
      userId,
      category,
      email,
      mobile,
      subject,
      content,
      fileName,
    });

    verifyAuthorization(response);
    return response;
  };
  const GetdataHelpManagement = async (
    userId,
    filter,
    search,
    category,
    page = 0,
    limit = 10,
    query_type_id = 0,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/contact/GetContactData",
      {
        userId,
        filter,
        search,
        category,
        page,
        limit,
        query_type_id,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const GetcategorylistTool = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/getcategorylistTool ",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const UpdateResponse = async (
    userId,
    message,
    PrimaryId,
    status,
    userIds,
    email,
    query_type_id,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/UpdateResponse", {
      userId,
      message,
      PrimaryId,
      status,
      userIds,
      email,
      query_type_id,
    });
    verifyAuthorization(response);
    return response;
  };
  const Displayresponse = async (userId, userid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/Displayresponse", {
      userId,
      userid,
    });
    verifyAuthorization(response);
    return response;
  };

  const InsertHabit = async (
    userId,
    date,
    habit_id,
    users_Id,
    freq,
    Year,
    Month,
  ) => {
    console.log(date, habit_id, users_Id, freq, Year);
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/InsertHabit",
      { userId, date, habit_id, users_Id, freq, Year, Month },
    );
    return response;
  };

  const GetHabitsList = async (userId, curmonth) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/GetHabitsList",
      { userId, curmonth },
    );
    return response;
  };
  const Gethabitsdata = async (userId, curmonth, curyear, user_id) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/Gethabitsdata",
      { userId, curmonth, curyear, user_id },
    );
    return response;
  };

  const DailyHabitUserData = async (userId, curmonth, curyear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/DailyHabitsListData",
      { userId, curmonth, curyear },
    );
    return response;
  };
  const WeeklyHabitUserData = async (userId, curmonth, curyear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/WeeklyHabitsListData",
      { userId, curmonth, curyear },
    );
    return response;
  };
  const QuaterlyHabitUserData = async (userId, curyear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/QuaterlyHabitsListData",
      { userId, curyear },
    );
    return response;
  };
  const OtherHabitUserData = async (userId, curmonth, curyear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/OtherHabitsListData",
      { userId, curmonth, curyear },
    );
    return response;
  };
  const handleChangedeletedata = async (userId, habit_id, freq, date) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/Habitdeletedata",
      { userId, habit_id, freq, date },
    );
    return response;
  };
  const habits = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/habit/habits", {
      userId,
    });
    verifyAuthorization(response);
    return response.data;
  };
  const habitchart = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/habitcharts",
    );
    verifyAuthorization(response);
    return response.data;
  };
  const CheckStatusOfHabit = async (userId, curyear, curmonth) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/HabitsChart",
      { userId, curyear, curmonth },
    );
    verifyAuthorization(response);
    return response.data;
  };
  const MonthlyLineChart = async (userId, curyear, curmonth) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/HabitsMonthChart",
      { userId, curyear, curmonth },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const GetHabitsNamelist = async (userId, UserId, Month, Year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/habit/GetHabitsNamelist",
      { userId, UserId, Month, Year },
    );
    verifyAuthorization(response);
    return response;
  };
  const CatcusDeleteCoreWatchlist = async (userId, wid, companyid) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/Catcusdeletecorewatchlisthistory",
      { userId, wid, companyid },
    );
  };

  const Catcuswatchlisthistory = async (userId, weeknum, CurrentYear) => {
    console.log("set", userId, weeknum, CurrentYear);
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/CatcusWatchlistGetdata",
      {
        userId,
        weeknum,
        CurrentYear,
      },
    );

    return response;
  };

  const GetAllCompanyforStockAlert = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/GetStockalertcompany",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  // const CreateStockalert = async (alert_name,userId,companyId,profit_target,stop_loss,expiry_date) => {
  //   const serviceToken = window.localStorage.getItem("serviceToken");
  //   axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  //   const response = await Axios.post(SERVER_API_URL+ "/api/stockAlert/CreateStockalert",{alert_name,userId,companyId,profit_target,stop_loss,expiry_date});
  //   verifyAuthorization(response);
  //   return response;
  // };
  const CreateStockalert = async (
    alert_name,
    userId,
    companyId,
    profit_target,
    stop_loss_per,
    profit_target_per,
    stop_loss,
    expiry_date,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/CreateStockalert",
      {
        alert_name,
        userId,
        companyId,
        profit_target,
        stop_loss_per,
        profit_target_per,
        stop_loss,
        expiry_date,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const GetStockalertCount = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/GetStockalertCount",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const GetStockalertList = async (userId, status) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/GetStockalertList",
      { userId, status },
    );
    verifyAuthorization(response);
    return response;
  };

  const DeleteExpired = async (userId, status) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/DeleteExpired",
      { userId, status },
    );
    verifyAuthorization(response);
    return response;
  };

  const StockAlertDelete = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/alertDelete",
      { userId, id },
    );
    verifyAuthorization(response);
    return response;
  };

  // const UpdateStockAlert = async (id,stop_loss,expiry_date,profit_target,alert_name) => {
  //   const serviceToken = window.localStorage.getItem("serviceToken");
  //   axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  //   const response = await Axios.post(SERVER_API_URL+ "/api/stockAlert/UpdateStockAlert",{id,stop_loss,expiry_date,profit_target,alert_name});
  //   verifyAuthorization(response);
  //   return response;
  // };
  const UpdateStockAlert = async (
    userId,
    id,
    stop_loss,
    stop_loss_per,
    expiry_date,
    profit_target,
    profit_target_per,
    alert_name,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/stockAlert/UpdateStockAlert",
      {
        userId,
        id,
        stop_loss,
        stop_loss_per,
        expiry_date,
        profit_target,
        profit_target_per,
        alert_name,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  function verifyAuthorization(response) {
    console.log(response.data.statuscode);
    if (response.data.statuscode === 401) {
      logout();
      window.location.href = window.location.origin + "/login";
    } else {
      return true;
    }
  }

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  const Gamefications = async (userId, days) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/Gamefication", {
      userId,
      days,
    });
    verifyAuthorization(response);
    return response;
  };

  /*
   * Company Weekly Flag Management — the admin module and the member read.
   *
   * One method per endpoint on the server's weeklyFlags.routes.js, in the shape
   * the rest of this file uses: read serviceToken, set the header, post, check
   * authorization, hand back the whole response (callers read `response.data`).
   */
  const withServiceToken = () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  };

  // The member page. The server pivots the last seven weeks that have data and
  // returns the window it used alongside the rows, so the table's headings and
  // its dots can never describe different weeks.
  const getMyList = async (params = {}) => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/mylist", params);
    verifyAuthorization(response);
    return response;
  };

  const getMyListFilters = async () => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/mylist/filters", {});
    verifyAuthorization(response);
    return response;
  };

  // The admin table.
  const getWeeklyFlagsList = async (params = {}) => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/weeklyflags/list", params);
    verifyAuthorization(response);
    return response;
  };

  const getWeeklyFlagsMeta = async () => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/weeklyflags/meta", {});
    verifyAuthorization(response);
    return response;
  };

  // Paged: the dialog passes { search, page, pageSize } and asks for the next
  // page as its listbox is scrolled.
  const searchWeeklyFlagCompanies = async (params = {}) => {
    withServiceToken();
    const response = await Axios.post(
      SERVER_API_URL + "/api/weeklyflags/company-search",
      params,
    );
    verifyAuthorization(response);
    return response;
  };

  // What one company already holds in the weeks it is asked about: the caller
  // sends the list, because the dialog's dropdown and this answer have to be
  // about the same weeks. The server used to pick the range itself (the window
  // the member list is showing), which is a different set of weeks from the
  // calendar the dialog offers — the two differed by the week ahead, so a
  // company flagged there came back holding nothing in a week it held a reading
  // in.
  const getWeeklyFlagCompanyWeeks = async (companyId, weekCodes) => {
    withServiceToken();
    const response = await Axios.post(
      SERVER_API_URL + "/api/weeklyflags/company-weeks",
      { companyId, weekCodes },
    );
    verifyAuthorization(response);
    return response;
  };

  const saveWeeklyFlag = async (payload) => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/weeklyflags/create", payload);
    verifyAuthorization(response);
    return response;
  };

  const updateWeeklyFlag = async (payload) => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/weeklyflags/update", payload);
    verifyAuthorization(response);
    return response;
  };

  // Takes a company, not a row: the admin table lists a flag record per week, and
  // Delete on one of those removes every week the company has.
  // Remove a company's flags — every one it holds, or only `weekCode`'s when a
  // week is named, which is the Edit dialog's way of taking back one reading.
  const deleteWeeklyFlag = async (companyId, weekCode) => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/weeklyflags/delete", {
      companyId,
      weekCode,
    });
    verifyAuthorization(response);
    return response;
  };

  const bulkUpdateWeeklyFlags = async (payload) => {
    withServiceToken();
    const response = await Axios.post(
      SERVER_API_URL + "/api/weeklyflags/bulk-update",
      payload,
    );
    verifyAuthorization(response);
    return response;
  };

  // Companies again, not the selected rows — the server de-duplicates them.
  //
  // `weekCode` narrows it the way it narrows `deleteWeeklyFlag` above: with a
  // week named, only that week's reading goes for each company, which is the
  // Bulk dialog's "No flag". Without one this is the selection's Delete button
  // and removes everything the selected companies hold. The key is dropped by
  // `JSON.stringify` when it is undefined, so an unnamed week arrives as a
  // request that never mentioned one.
  const bulkDeleteWeeklyFlags = async (companyIds, weekCode) => {
    withServiceToken();
    const response = await Axios.post(SERVER_API_URL + "/api/weeklyflags/bulk-delete", {
      companyIds,
      weekCode,
    });
    verifyAuthorization(response);
    return response;
  };

  // The CSV goes up as multipart, so it takes the FormData shape the rest of
  // this file uses for uploads rather than a JSON body.
  const uploadWeeklyFlagsCsv = async (file) => {
    withServiceToken();
    const formData = new FormData();
    formData.append("file", file);
    const response = await Axios.post(
      SERVER_API_URL + "/api/weeklyflags/upload-csv",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    verifyAuthorization(response);
    return response;
  };

  const Onboarding = async (userId, id, groupId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/OnBoarding", {
      userId,
      id,
      groupId,
    });
    verifyAuthorization(response);
    return response;
  };

  // const OnboardingCheckList_Added = async (
  //   id,
  //   selectedMark,
  //   title_id,
  //   sub_id
  // ) => {
  //   const serviceToken = window.localStorage.getItem("serviceToken");
  //   axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  //   const response = await Axios.post(
  //     SERVER_API_URL + "/api/OnBoarding/OnboardingCheckList_Added",
  //     { id, selectedMark, title_id, sub_id }
  //   );
  //   verifyAuthorization(response);
  //   return response;
  // };
  // const OnboardingCheckList_Delete_OR_Edit = async (
  //   id,
  //   selectedMark,
  //   title_id,
  //   sub_id
  // ) => {
  //   const serviceToken = window.localStorage.getItem("serviceToken");
  //   axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  //   const response = await Axios.post(
  //     SERVER_API_URL + "/api/OnBoarding/OnboardingCheckList_Delete_OR_Edit",
  //     { id, selectedMark, title_id, sub_id }
  //   );
  //   verifyAuthorization(response);
  //   return response;
  // };

  const OnboardingCheckList_Added = async (id, title_id, sub_id, Marks) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/OnBoarding/OnboardingCheckList_Added",
      { id, title_id, sub_id, Marks },
    );
    verifyAuthorization(response);
    return response;
  };
  const OnboardingCheckList_Delete_OR_Edit = async (
    id,
    title_id,
    sub_id,
    Marks,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/OnBoarding/OnboardingCheckList_Delete_OR_Edit",
      { id, title_id, sub_id, Marks },
    );
    verifyAuthorization(response);
    return response;
  };

  const OnboardingPopupStatus = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/OnBoarding/OnboardingPopupStatus",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const OnboardingPopupDontShowAgain = async (userId, dontShowAgain) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/OnBoarding/OnboardingPopupDontShowAgain",
      { userId, dontShowAgain },
    );
    verifyAuthorization(response);
    return response;
  };
  // Links for the Diamond Partner Program card, served from the
  // diamond_partner_program table.
  const DiamondPartnerProgram = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/OnBoarding/DiamondPartnerProgram",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const GetCalculatorSettings = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/Calculator/getCalculatorSettings",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const SaveCalculatorSettings = async (userId, settings) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/Calculator/saveCalculatorSettings",
      { userId, ...settings },
    );
    verifyAuthorization(response);
    return response;
  };

  const UserDetailsQic = async (
    userId,
    search,
    SEmail,
    SHackothonFinisher,
    STechnofundaAward,
    SGroup,
    SCommunityDuration,
    SIndustry,
    STeamName,
    STeamPosition,
    GroupFilterstate,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/userdetailQic",
      {
        userId,
        search,
        SEmail,
        SHackothonFinisher,
        STechnofundaAward,
        SGroup,
        SCommunityDuration,
        SIndustry,
        STeamName,
        STeamPosition,
        GroupFilterstate,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const MemberDetails = async (teamname, event_year, currentActiveEvent) => {
    const response = await Axios.post(SERVER_API_URL + "/api/qic/teamname", {
      teamname,
      event_year,
      currentActiveEvent,
    });
    verifyAuthorization(response);
    return response;
  };
  const StageAttendDetails = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/stageattenddetails",
      {
        userId,
        value,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const QICSemifinalist = async (stageId, userId, event_year) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/semifinalist",
      { stageId, userId, event_year },
    );
    verifyAuthorization(response);
    return response;
  };
  const ShortlistStatusNextRound = async (userId, value, StageId) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/shortliststatusnextround",
      {
        userId,
        value,
        StageId,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const QicUploadData = async (userId, StageId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/qicuploaddata",
      {
        userId,
        StageId,
        value,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const TeamdetailsdataQic = async (STeamName) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/teamdetailQic",
      { STeamName },
    );
    verifyAuthorization(response);
    return response;
  };

  const EditdatauserQic = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/EditdatauserQic",
      { userId, value },
    );
    verifyAuthorization(response);
    return response;
  };
  const TeamEditDetailsData = async (userId, value, event_year) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/teamNameEditGetData",
      { userId, value, event_year },
    );
    verifyAuthorization(response);
    return response;
  };
  const DocumentdetailsdataQic = async (
    userId,
    filterTeamName,
    filterStageName,
    filtershortlist,
    filterEventName,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/docdetailQic",
      {
        userId,
        filterTeamName,
        filterStageName,
        filtershortlist,
        filterEventName,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const RegisterCheck = async (userId, event_year) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/registercheck",
      { userId, event_year },
    );
    verifyAuthorization(response);
    return response;
  };

  const EditTeamQic = async (value) => {
    const response = await Axios.post(SERVER_API_URL + "/api/qic/EditTeamQic", {
      value,
    });
    verifyAuthorization(response);
    return response;
  };
  const EditDocQic = async (userId, value, value1) => {
    const response = await Axios.post(SERVER_API_URL + "/api/qic/EditDocQic", {
      userId,
      value,
      value1,
    });
    verifyAuthorization(response);
    return response;
  };

  const EditSavedataQic = async (
    userId,
    ValueCheck,
    emaildata,
    fnamedata,
    isPosition,
    Team,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/EditsavedataQic",
      {
        userId,
        ValueCheck,
        emaildata,
        fnamedata,
        isPosition,
        Team,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditsavedTeamQic = async (
    ValueCheck,
    Week1,
    Week2,
    Week3,
    Week4,
    Week5,
    Week6,
    Week7,
    Week8,
    Team,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/EditsavedteamQic",
      {
        ValueCheck,
        Week1,
        Week2,
        Week3,
        Week4,
        Week5,
        Week6,
        Week7,
        Week8,
        Team,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditsaveddocQic = async (
    userId,
    ValueCheck,
    Status,
    Team,
    Stageid,
    teamId,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/EditsaveddocQic",
      {
        userId,
        ValueCheck,
        Status,
        Team,
        Stageid,
        teamId,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const SaveCreateTeam = async (userId, createTeam) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/SaveCreateTeam",
      { userId, createTeam },
    );
    verifyAuthorization(response);
    return response;
  };
  const SaveCreateEvent = async (
    userId,
    CreateEvent,
    EventDesc,
    eventStartDate,
    eventEndDate,
    EventYear,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/SaveCreateEvent",
      {
        userId,
        CreateEvent,
        EventDesc,
        eventStartDate,
        eventEndDate,
        EventYear,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const SaveRenameTeam = async (userId, RenameTeam, OriginalTeamName) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/SaveRenameTeam",
      { userId, RenameTeam, OriginalTeamName },
    );
    verifyAuthorization(response);
    return response;
  };

  const UserTeamDetails = async () => {
    const response = await Axios.get(SERVER_API_URL + "/api/teams");
    verifyAuthorization(response);
    return response;
  };
  const StageShortlistTeamDetails = async (userId, event_year, page = 1, limit = 10) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/teamshortlistlist",
      { userId, event_year, page, limit },
    );
    verifyAuthorization(response);
    return response;
  };

  const QicEventDetails = async (userId) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/eventdetails",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const QicEventDetailsSave = async (
    userId,
    CurEventName,
    startDate,
    endDate,
    CurEventYear,
    CurEventStatus,
    linkshow,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/eventdetailssave",
      {
        userId,
        CurEventName,
        startDate,
        endDate,
        CurEventYear,
        CurEventStatus,
        linkshow,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const EventName = async () => {
    const response = await Axios.get(SERVER_API_URL + "/api/qic/eventsname");
    verifyAuthorization(response);
    return response;
  };

  const QicStageDetailsSave = async (
    userId,
    startDate,
    endDate,
    valueCheck,
    event_year,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/savedstagedata",
      { userId, startDate, endDate, valueCheck, event_year },
    );
    verifyAuthorization(response);
    return response;
  };

  const EditStageData = async (userId, value, event_year) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/editstagedata",
      { userId, value, event_year },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditActiveEvent = async (userId, activestage, CurEventName) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/editactiveevent",
      { userId, activestage, CurEventName },
    );
    verifyAuthorization(response);
    return response;
  };
  const SendMessageToAdmins = async (
    userId,
    emails,
    mobilenumber,
    category,
    subject,
    content,
    attachment,
    userid,
    categoryIDs,
    helpstatus,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/Sendmessagetoadmins",
      {
        userId,
        emails,
        mobilenumber,
        category,
        subject,
        content,
        attachment,
        userid,
        categoryIDs,
        helpstatus,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const FeatureRequestsGetList = async (filters) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/list",
      filters,
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsGetDevelopers = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/developers",
      {},
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsGetTesters = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/testers",
      {},
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsGetQueryTypes = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/queryTypes",
      {},
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsGetEnvStatuses = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/environmentStatuses",
      {},
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsGetQueryDetail = async (params) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/queryDetail",
      params,
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsUpdate = async (data) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/update",
      data,
    );
    verifyAuthorization(response);
    return response;
  };
  const FeatureRequestsDelete = async (data) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/featureRequests/delete",
      data,
    );
    verifyAuthorization(response);
    return response;
  };

  const postOnboardingAdmin = async (path, data) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + `/api/onboardingAdmin/${path}`,
      data || {},
    );
    verifyAuthorization(response);
    return response;
  };

  const onboardingAdminGetStages = async () => postOnboardingAdmin("stages");
  const onboardingAdminSaveStage = async (data) =>
    postOnboardingAdmin("stage/save", data);
  const onboardingAdminDeleteStage = async (data) =>
    postOnboardingAdmin("stage/delete", data);
  const onboardingAdminGetItems = async (data) =>
    postOnboardingAdmin("items", data);
  const onboardingAdminSaveItem = async (data) =>
    postOnboardingAdmin("item/save", data);
  const onboardingAdminDeleteItem = async (data) =>
    postOnboardingAdmin("item/delete", data);
  const onboardingAdminSetStatus = async (data) =>
    postOnboardingAdmin("status", data);
  const onboardingAdminReorder = async (data) =>
    postOnboardingAdmin("reorder", data);

  const AddedQicUser = async (
    userId,
    auEmail,
    auHackothonFinisher,
    auTechnofundaaward,
    auCommunityDuration,
    auIndustry,
    currentActiveEvent,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/addedqicuser",
      {
        userId,
        auEmail,
        auHackothonFinisher,
        auTechnofundaaward,
        auCommunityDuration,
        auIndustry,
        currentActiveEvent,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const DeleteSelectedData = async (userId, value) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/deleteuserval",
      { userId, value },
    );
    verifyAuthorization(response);
    return response;
  };

  const BusinessRanking_Quiz_Questions = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/user/brquiz", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const BusinessRanking_Quiz_Questions_Answer = async (
    userId,
    data,
    User_Id,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/brquizanswer",
      { userId, data, User_Id },
    );
    verifyAuthorization(response);
    return response;
  };
  const BusinessRanking_Quiz_Questions_Answer_Check = async (
    userId,
    User_Id,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/brquizanswercheck",
      { userId, User_Id },
    );
    verifyAuthorization(response);
    return response;
  };

  const get10Y = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/user/10Y_yield", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

  const Voting_Insert = async (userId, teamid, type, isType, event_year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/votinginsert",
      {
        userId,
        teamid,
        type,
        isType,
        event_year,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const QicVotingDetails = async (userId, event_year) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/votingdetails",
      { userId, event_year },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditVotingQic = async (userId, event_year) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/editvotingqic",
      { userId, event_year },
    );
    verifyAuthorization(response);
    return response;
  };
  const QicVotingDetailsSave = async (
    userId,
    voteLimitSemifinaledit,
    likepointsemifinaledit,
    dislikePointSemifinaledit,
    startDateEdit,
    endDateEdit,
    event_year,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/votingdetailssave",
      {
        userId,
        voteLimitSemifinaledit,
        likepointsemifinaledit,
        dislikePointSemifinaledit,
        startDateEdit,
        endDateEdit,
        event_year,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const QicVotingDetailsSaveFinal = async (
    userId,
    voteLimitfinaledit,
    likepointfinaledit,
    dislikepointfinaledit,
    startDateEdit,
    endDateEdit,
    event_year,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/votingdetailssavefinal",
      {
        userId,
        voteLimitfinaledit,
        likepointfinaledit,
        dislikepointfinaledit,
        startDateEdit,
        endDateEdit,
        event_year,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const AllVotePoints = async (userId, user_id, types, event_year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/qic/votepoints", {
      userId,
      user_id,
      types,
      event_year,
    });
    verifyAuthorization(response);
    return response;
  };

  const PresentedByName = async (userId, Presentname) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/Event/Presentname",
      {
        userId,
        Presentname,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const ColorFlagsSet = async (userId, selectedValue) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/corewaitch/ColorFlagsSet",
      { userId, selectedValue },
    );
    verifyAuthorization(response);
    return response;
  };
  const AddCompanyflag = async (
    userId,
    selectMonth,
    selectedValue,
    selectYear,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/corewaitch/AddCompanyflag",
      { userId, selectMonth, selectedValue, selectYear },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditcoreWatchlist = async (
    userId,
    UserId,
    companyId,
    EditCompany_id,
    Editcompany_name,
    Editmonth,
    Editcolor,
    Timeframe,
    year,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/corewaitch/EditcoreWatchlist",
      {
        userId,
        UserId,
        companyId,
        EditCompany_id,
        Editcompany_name,
        Editmonth,
        Editcolor,
        Timeframe,
        year,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const corewatchlisthistoryAdmin = async (userId, searchTerm,selectMonth, selectYear) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreWatchlist/GetdataAdmin ",
      {
        userId,
        searchTerm,
        selectMonth,
        selectYear,
      },
    );

    return response;
  };
  const DownLoadCoreWhatchList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/corewaitch/DownLoadCoreWhatchList",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const DocumentdetailsdataQicWinner = async (
    userId,
    filterTeamName,
    filterStageName,
    filtershortlist,
    filterEventName,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/docdetailQicWinner",
      {
        userId,
        filterTeamName,
        filterStageName,
        filtershortlist,
        filterEventName,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const EditDocQicWinner = async (userId, value, value1) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/EditDocQicWinner",
      {
        userId,
        value,
        value1,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const EditsaveddocQicWinner = async (
    userId,
    ValueCheck,
    winnerStatus,
    Team,
    Stageid,
    teamId,
  ) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/EditsaveddocQicWinner",
      {
        userId,
        ValueCheck,
        winnerStatus,
        Team,
        Stageid,
        teamId,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const GetLoginDemoVideoLink = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/getlogindemovideoLink",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const GetDemoVideoLink = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/getdemovideoLink",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const AddDeomvideolink = async (userId, user_id, linkname, videolink) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/adddeomvideolink",
      { userId, user_id, linkname, videolink },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditDemoVideoLink = async (
    userId,
    user_id,
    linkname,
    videolink,
    EditId,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/editvideolink", {
      userId,
      user_id,
      linkname,
      videolink,
      EditId,
    });
    verifyAuthorization(response);
    return response;
  };
  const DeletVideoLink = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/deletvideolink", {
      userId,
      id,
    });
    verifyAuthorization(response);
    return response;
  };

  const VideolinkAddCoreWatichList = async (
    userId,
    compnayId,
    videoName,
    videoLink,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/videolinkAddCoreWatichList",
      { userId, compnayId, videoName, videoLink },
    );
    verifyAuthorization(response);
    return response;
  };
  // video link Data get
  const VideoLinkDatas = async (userId, companyId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/videoLinkDatas", {
      userId,
      companyId,
    });
    verifyAuthorization(response);
    return response;
  };
  //video link update
  const UpdateVideo_Link = async (userId, id, name, videolink) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/updatevideo_Link",
      { userId, id, name, videolink },
    );
    verifyAuthorization(response);
    return response;
  };

  // video link delete
  const DeleteVideoLink = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/deleteVideoLink", {
      userId,
      id,
    });
    verifyAuthorization(response);
    return response;
  };

  // pdf link add
  const PdflinkAddCoreWatichList = async (
    userId,
    compnayId,
    PdfName,
    PdfLink,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/PdflinkAddCoreWatichList",
      { userId, compnayId, PdfName, PdfLink },
    );
    verifyAuthorization(response);
    return response;
  };
  // Pdf link Data get
  const PdfLinkDatas = async (userId, companyId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/PdfLinkDatas", {
      userId,
      companyId,
    });
    verifyAuthorization(response);
    return response;
  };
  //Pdf link update
  const UpdatePdf_Link = async (userId, id, name, Pdflink) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/updatePdf_Link", {
      userId,
      id,
      name,
      Pdflink,
    });
    verifyAuthorization(response);
    return response;
  };

  // Pdf link delete
  const DeletePdfLink = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/deletePdfLink", {
      userId,
      id,
    });
    verifyAuthorization(response);
    return response;
  };

  const RewardsPointGet_user = async (userId, user_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/RewardsPointGet_user",
      { userId, user_id },
    );
    verifyAuthorization(response);
    return response;
  };

  const ApiData = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/get_NSEdata");
    verifyAuthorization(response);
    return response;
  };

  const loginRewards = async (user_id) => {
    const response = await Axios.post(SERVER_API_URL + "/api/loginRewards", {
      user_id,
    });
    return response;
  };

  const NoteDateCorewatichlist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/corewatichlist/Note_color_core_watchList",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const RewardCategorys = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/Reward/RewardCategorys",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const IdeaDashboard = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/IdeaDashboard", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

  const UserIdeaReward = async (userId, user_Id, year, week) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SateliteWatchlist/UserIdeaReward",
      { userId, user_Id, year, week },
    );
    verifyAuthorization(response);
    return response;
  };

  //Book hive
  const DeleteBook = async (id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/DeleteBook", {
      id,
    });
    verifyAuthorization(response);
    return response;
  };

  const DeleteImage = async (id, file) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/delete-image", {
      id,
      file,
    });
    verifyAuthorization(response);
    return response;
  };

  const DeleteFile = async (id, file) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/delete-File", {
      id,
      file,
    });
    verifyAuthorization(response);
    return response;
  };

  const EditBook = async (data) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/EditBook", {
      data,
    });
    verifyAuthorization(response);
    return response;
  };

  const LearningList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/user/Learnings");
    verifyAuthorization(response);
    return response;
  };

  //SECTOR WATCHLIST
  const SectorsList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/sectorslist",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const InsertSectorwatchlist = async (
    userId,
    sectorID,
    currentDate,
    currentyear,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/addSectorwatchlist",
      {
        userId,
        sectorID,
        currentDate,
        currentyear,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const Sectorwatchlistdata = async (userId, weeknum, CurrentYear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/getSectorWatchlistHistory",
      {
        userId,
        weeknum,
        CurrentYear,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const DeleteSectorWatchlist = async (userId, wid, sectorID) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/watchlist/deleteSectorwatchlisthistory",
      {
        userId,
        wid,
        sectorID,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const UserSectorRewards = async (userId, user_Id, year, week) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SateliteWatchlist/UserSectorRewards",
      { userId, user_Id, year, week },
    );
    verifyAuthorization(response);
    return response;
  };

  const ProfileImg = async (userId, memberId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await axios.post("/api/profile_img", {
      userId,
      memberId,
    });
    verifyAuthorization(response);
    return response;
  };

  const SectorLeaderWatch = async (userId, startDate, endDate) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SectorLeaderWatch",
      { userId, startDate, endDate },
    );
    verifyAuthorization(response);
    return response;
  };

  const SectorLeaderShortList = async (userId, year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SectorLeaderShortList",
      { userId, year },
    );
    verifyAuthorization(response);
    return response;
  };
  const EditSLWatchlist = async (userId, company_id, year, month, colors) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/EditSLWatchlist", {
      userId,
      company_id,
      year,
      month,
      colors,
    });
    verifyAuthorization(response);
    return response;
  };
  const SLWatchListDelete = async (userId, company_id, year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SLWatchListDelete",
      { userId, company_id, year },
    );
    verifyAuthorization(response);
    return response;
  };
  const SLWatchListInsert = async (userId, companyId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SLWatchListInsert",
      { userId, companyId },
    );
    verifyAuthorization(response);
    return response;
  };
  const ShortListCommentsAdd = async (
    userId,
    companyId,
    year,
    category,
    comments,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/ShortListCommentsAdd",
      { userId, companyId, year, category, comments },
    );
    verifyAuthorization(response);
    return response;
  };
  const BulkUploaderWatchlist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SectorLeaderWatchlistData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const Calculate2G3G = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SectorLeaderShortList3G2G",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  //COMMUNITY NOTES
  const CompanyData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/CompanyData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const CompanyQuarterData = async (userId, company_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/CompanyQuarterData",
      { userId, company_id },
    );
    verifyAuthorization(response);
    return response;
  };

  const UserNotes = async (userId, user_id, companyId, Quarter, Year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/UserNotes",
      { userId, user_id, companyId, Quarter, Year },
    );
    verifyAuthorization(response);
    return response;
  };

  const UpdateLink = async (
    userId,
    sentiment,
    ranking,
    pptLink,
    materialLink,
    transcriptLink,
    companyId,
    Quarter,
    Year,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/UpdateLink",
      {
        userId,
        sentiment,
        ranking,
        pptLink,
        materialLink,
        transcriptLink,
        companyId,
        Quarter,
        Year,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const UpdateNotes = async (
    userId,
    notes,
    companyId,
    Quarter,
    Year,
    NoteId,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/UpdateNotes",
      { userId, notes, companyId, Quarter, Year, NoteId },
    );
    verifyAuthorization(response);
    return response;
  };

  const AddUserNotes = async (userId, notes, companyId, Quarter, Year) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/AddUserNotes",
      { userId, notes, companyId, Quarter, Year },
    );
    verifyAuthorization(response);
    return response;
  };

  const SectorIdeasComment = async (userId, comment, company_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken"); // Adjust based on where you store your token
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SectorIdeasComment",
      { userId, comment, company_id },
    );
    verifyAuthorization(response);
    return response;
  };

  // Community Notes
  const getuserslist = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/getuserslist",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const AddContributors = async (userId, Id, companyId, roleId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

    try {
      const response = await Axios.post(
        SERVER_API_URL + "/api/CommunityNotes/AddContributors",
        {
          userId,
          Id,
          companyId,
          roleId,
        },
      );
      verifyAuthorization(response); // Ensure this is used appropriately
      return response;
    } catch (error) {
      // Handle errors and rethrow with the appropriate message
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error("An error occurred while adding contributor.");
      }
    }
  };
  const showCompanyList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/showCompanyList",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const showContribtorData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/showContribtorData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const deleteContribtorData = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/deleteContribtorData",
      { userId, id },
    );
    verifyAuthorization(response);
    return response;
  };
  const updateContributor = async (userId, userid, companyId, roleId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

    try {
      const response = await Axios.post(
        SERVER_API_URL + "/api/CommunityNotes/updateContributor",
        {
          userId,
          userid,
          companyId,
          roleId,
          id,
        },
      );

      verifyAuthorization(response); // Verify if the response is authorized
      return response;
    } catch (error) {
      // Throw error with detailed message from response
      if (error.response && error.response.data) {
        throw new Error(
          error.response.data.message || "An unknown error occurred.",
        );
      } else {
        throw new Error(error.message || "An unknown error occurred.");
      }
    }
  };
  const showNotesData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/showNotesData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const publishNote = async (
    userId,
    Id,
    roleId,
    companyId,
    noteId,
    quarter,
    year,
    financials,
    strategy,
    risk,
    outlook,
    others,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/publishNote",
      {
        userId,
        Id,
        roleId,
        companyId,
        noteId,
        quarter,
        year,
        financials,
        strategy,
        risk,
        outlook,
        others,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const updateNoteHistory = async (
    userId,
    companyId,
    noteId,
    quarter,
    year,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/updateNoteHistory",
      { userId, companyId, noteId, quarter, year },
    );
    verifyAuthorization(response);
    return response;
  };

  const UpdateAbout = async (userId, companyId, about, websitelink) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/UpdateAbout",
      { userId, companyId, about, websitelink },
    );
    verifyAuthorization(response);
    return response;
  };

  const CompanyList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/CompanyList",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const AddCompany = async (userId, company_id, company_name) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/AddCompany",
      { userId, company_id, company_name },
    );
    verifyAuthorization(response);
    return response;
  };
  const DeleteCompany = async (userId, company_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/DeleteCompany",
      { userId, company_id },
    );
    verifyAuthorization(response);
    return response;
  };
  const updatemonthstatus = async (month, year, color_id, company_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/updatemonthstatus",
      { month, year, color_id, company_id },
    );
    verifyAuthorization(response);
    return response;
  };

  // const coreIdeascuesupdate = async () => {
  //   const serviceToken = window.localStorage.getItem("serviceToken");
  //   Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  //   const response = await Axios.get(SERVER_API_URL + "/api/coreIdeascuesupdate", {});
  //   verifyAuthorization(response);
  //   return response;
  // };
  const coreIdeastrendscoreupdate = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/coreIdeastrendscoreupdate",
      {},
    );
    verifyAuthorization(response);
    return response;
  };
  const Updateideacoretable = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SectorLeaderWatchlistData",
      {},
    );
    verifyAuthorization(response);
    return response;
  };
  const Top30IdeaCore = async (userId, startDate, endDate) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/Top30IdeaCore", {
      userId,
      startDate,
      endDate,
    });
    verifyAuthorization(response);
    return response;
  };

  const IdeaSatellite = async (
    userId,
    startDate,
    Turnover,
    ThreShold,
    duration,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SateliteWatchlist/IdeaSateliteCompany",
      { userId, startDate, Turnover, ThreShold, duration },
    );
    verifyAuthorization(response);
    return response;
  };

  const IdeaSatelliteSection = async (
    userId,
    startDate,
    duration,
    Turnover,
    ThreShold,
    sectionTab,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/IdeaSateliteCompanysection",
      { userId, startDate, duration, Turnover, ThreShold, sectionTab },
    );
    verifyAuthorization(response);
    return response;
  };

  const editAccess = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/editAccess", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

  // Strong Calculator
  const Strong_Quiz_Questions = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/user/strongquiz", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const Strong_Quiz_Questions_Answer = async (userId, data, User_Id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/strongquizanswer",
      { userId, data, User_Id },
    );
    verifyAuthorization(response);
    return response;
  };
  const Strong_Quiz_Questions_Answer_Check = async (userId, User_Id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/strongquizanswercheck",
      { userId, User_Id },
    );
    verifyAuthorization(response);
    return response;
  };

  const PriceMarker = async (
    userId,
    start_date,
    end_date,
    old_start_date,
    old_end_date,
    market_type,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/price_marker",
      {
        userId,
        start_date,
        end_date,
        old_start_date,
        old_end_date,
        market_type,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const PriceMarkerSector = async (
    userId,
    start_date,
    end_date,
    old_start_date,
    old_end_date,
    index_type,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/user/price_marker_sector",
      {
        userId,
        start_date,
        end_date,
        old_start_date,
        old_end_date,
        index_type,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const CMQuarterData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/CMQuarterData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const CMQuarterDataInsert = async (userId, year, quarter) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/CMQuarterDataInsert",
      { userId, year, quarter },
    );
    verifyAuthorization(response);
    return response;
  };

  const CMQuarterDataDelete = async (userId, rowId, year, quarter) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/CommunityNotes/CMQuarterDataDelete",
      { userId, rowId, year, quarter },
    );
    verifyAuthorization(response);
    return response;
  };

  
  const HallOfFame = async ({ badgeType, page = 1, limit = 18, search = "" } = {}) => {
    try {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const params = new URLSearchParams();
      if (badgeType) params.append("badge", badgeType);
      params.append("page", page);
      params.append("limit", limit);
      if (search) params.append("search", search);
      const url = SERVER_API_URL + `/api/user/hallOfFame?${params.toString()}`;
      const response = await Axios.get(url);
      verifyAuthorization(response);
      return response.data;
    } catch (error) {
      console.error("Error fetching Hall of Fame data:", error);
      throw error;
    }
  };
  const IdeaSectorWc = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/sector/Idea_Sector_Wc",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const IdeaSatelliteOneWc = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/sector/Idea_SatelliteOne_Wc",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  // Wellness Event
  const WellnessEventGetData = async (userId, filtervalues) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/WellnessEventGetData",
      { userId, filtervalues },
    );
    verifyAuthorization(response);
    return response;
  };
  const WellnessEventAdd = async (userId, data) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/WellnessEventAdd",
      { userId, data },
    );
    verifyAuthorization(response);
    return response;
  };
  const WellnessEventEdit = async (userId, data, eventid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/WellnessEventEdit",
      { userId, data, eventid },
    );
    verifyAuthorization(response);
    return response;
  };
  const WellnessEventDelete = async (userId, eventid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/WellnessEventDelete",
      { userId, eventid },
    );
    verifyAuthorization(response);
    return response;
  };
  // User Module
  const WellnessEventData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/WellnessEventData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const WellnessDailyEventData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/WellnessDailyEventData",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const QIC_popup_status = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/qic/QIC_popup_status",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const MarketHolidays = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/sector/MarketHolidays",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const IdeaSatelliteUploadData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/sector/IdeasSatelliteDB",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const activeUserDetails = async (userId) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/activeuserdetail",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const inactiveUserDetails = async (userId) => {
    const response = await Axios.post(
      SERVER_API_URL + "/api/user_management/inactiveuserdetail",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const SW_One_Percent_Download = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/SW_One_Percent_Download",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const TrendingSatelliteWatchlistDownload = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/TrendingSatelliteWatchlistDownload",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const TrendingCorePortfolioDownload = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/TrendingCorePortfolioDownload",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  //Book Club
  const BookClubEvent = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/BookClubEvent", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const BooksList = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/BooksList", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const DeleteBookData = async (userId, bookid) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/DeleteBookEnrty", {
      userId,
      bookid,
    });
    verifyAuthorization(response);
    return response;
  };
  const DeleteBookImage = async (bookId, file) => {
    const serviceToken = window.localStorage.getItem("serviceToken");

    if (!serviceToken) {
      console.error("Authorization token is missing");
      return { error: "Authorization token is missing" };
    }

    Axios.defaults.headers.common["Authorization"] = `Bearer ${serviceToken}`;

    try {
      const response = await Axios.post(
        SERVER_API_URL + "/api/DeleteBookImage",
        {
          bookId,
          file,
        },
      );

      verifyAuthorization(response);

      return response;
    } catch (error) {
      console.error("Error deleting book image:", error);
      return { error: error.message || "Error deleting book image" };
    }
  };
  const UpdateBookData = async (userId, bookdetail) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/UpdateBookDetail",
      { userId, bookdetail },
    );
    verifyAuthorization(response);
    return response;
  };
  const BookData = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/Books", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };

  const CoreauditdatalistAnalysis = async (userId, userIds) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/getcoreauditAnalysis",
      { userId, userIds },
    );
    verifyAuthorization(response);
    return response;
  };

  const getcoreauditMarketCapAnalysis = async (userId, userIds) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/getcoreauditMarketCapAnalysis",
      { userId, userIds },
    );
    verifyAuthorization(response);
    return response;
  };

  const FetchDwonloadDataRewards = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(SERVER_API_URL + "/api/export-rewards", {
      userId,
    });
    verifyAuthorization(response);
    return response;
  };
  const FetchDwonloadDataIdeaRewards = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/export-idearewards",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const FetchMaxRewardPoints = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/Reward/FetchMaxPoints",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  // Core Watch List Analysis
  const coreThemeAnalysis = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/CoreWatchList/ThemeAnalysis",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const coreCapAnalysis = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/CoreWatchList/CapAnalysis",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const coreWLAnalysis = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/CoreWatchList/Analysis",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const alertThemeAnalysis = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/alert/CoreWatchList/ThemeAnalysis",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const alertCapAnalysis = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/alert/CoreWatchList/CapAnalysis",
      {
        userId,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  //Top 5 companies Based On user Recommedtions
  const userAI = async (Friendid, CurrentWeekNumber, CurrentYear) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/DisplayUserAI", {
      Friendid,
      CurrentWeekNumber,
      CurrentYear,
    });
    verifyAuthorization(response);
    return response;
  };

  const IdeaDashboardYearToppers = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/rewards/IdeaDashboardYearToppers",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const IdeaDashboardAnnualToppers = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/rewards/IdeaDashboardAnnualToppers",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };

  const UtilityReportTableData = async (userId, startDate, endDate) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/utilityreports",
      { userId, startDate, endDate },
    );
    verifyAuthorization(response);
    return response;
  };

  const ModuleTraceInit = async (userId, module_name) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/report/module_trace",
      { userId, module: module_name },
    );
    verifyAuthorization(response);
    return response;
  };

  // university

  const universityCategory = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/fetchcategory",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const universitySubCategory = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/fetchSubcategory",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const universityCoach = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/fetchCoach",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const sumbitAddCoach = async (userId, values) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    // Build FormData
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("image", values.image);
    formData.append("name", values.name);
    formData.append("category", values.category);
    formData.append("signupLink", values.signupLink);
    formData.append("about", values.about);
    formData.append("sub_category", values.sub_category);
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/NewCoachAdd",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    verifyAuthorization(response);
    return response;
  };
  const sumbitEditCoach = async (userId, values) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    // Build FormData
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("previewImage_path", values.previewImage_path);
    formData.append("id", values.id);
    formData.append("image", values.image);
    formData.append("name", values.name);
    formData.append("category", values.category);
    formData.append("signupLink", values.signupLink);
    formData.append("about", values.about);
    formData.append("isimage", values.isimage);
    formData.append("sub_category", values.sub_category);
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/updateCoach",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const universityDelete = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/deleteCoach",
      { userId, id },
    );
    verifyAuthorization(response);
    return response;
  };
  const NewCategoryAdd = async (userId, payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/NewCategoryAdd",
      { userId, payload },
    );
    verifyAuthorization(response);
    return response;
  };
  const updateCategory = async (userId, id, name) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/updateCategory",
      { userId, id, name },
    );
    verifyAuthorization(response);
    return response;
  };
  const DeleteCategory = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/DeleteCategory",
      { userId, id },
    );
    verifyAuthorization(response);
    return response;
  };
  const NewSubCategoryAdd = async (userId, payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/NewSubCategoryAdd",
      { userId, payload },
    );
    verifyAuthorization(response);
    return response;
  };
  const updateSubCategory = async (userId, id, name) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/updateSubCategory",
      { userId, id, name },
    );
    verifyAuthorization(response);
    return response;
  };
  const DeleteSubCategory = async (userId, id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/university/DeleteSubCategory",
      { userId, id },
    );
    verifyAuthorization(response);
    return response;
  };

  const GetIdeasCoreList = async (
    userId,
    startDate,
    endDate,
    selectedCap,
    search,
    selectedFilter,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/ideascore/data", {
      userId,
      startDate,
      endDate,
      selectedCap,
      search,
      selectedFilter,
    });
    verifyAuthorization(response);
    return response;
  };

  const GetIdeasCoreList_FA = async (
    userId,
    startDate,
    endDate,
    selectedCap,
    search,
    selectedFilter,
    selectedFilterFA,
    selectedFilterMonthFA,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/ideascore/fundamental_data",
      {
        userId,
        startDate,
        endDate,
        selectedCap,
        searchValue: search,
        selectedFilter,
        selectedFilterFA,
        selectedFilterMonthFA,
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const GetTrendReason = async ({
    userId,
    company_id,
    currentDate,
    previousData,
  }) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/ideascore/fundamental_trent_reason",
      {
        userId,
        company_id,
        currentDate,
        previousData,
      },
    );
    verifyAuthorization(response);
    return response;
  };
  const GetCompanyDescReport = async ({ companyId, quarter }) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      `${SERVER_API_URL}/api/ideascore/companydesc-report/${companyId}/${quarter}`,
    );
    verifyAuthorization(response);
    return response;
  };

  const GetCompanyEarningsReport = async ({ companyId, quarter }) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    // const response = await Axios.post(
    //   SERVER_API_URL + "/api/ideascore/earnings-report",
    //   {
    //     companyId,
    //     quarter,
    //   }

    const response = await Axios.get(
      `${SERVER_API_URL}/api/earnings-report/${companyId}/${quarter}`,
    );

    verifyAuthorization(response);
    return response;
  };
  const GetAllCompanyList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/earnings-report/allcompanylist",
    );
    verifyAuthorization(response);
    return response;
  };

  const UploadEarningsReport = async (formdata) => {
    const serviceToken = window.localStorage.getItem("serviceToken");

    const apiUrl = `${SERVER_API_URL}/api/faOpenAi_data/faOpenAiUpload`;

    const response = await Axios.post(apiUrl, formdata, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${serviceToken}`,
      },
    });

    verifyAuthorization(response);
    return response;
  };

  const GetFAPerformanceCount = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(SERVER_API_URL + "/api/fa_analysis");
    verifyAuthorization(response);
    return response;
  };

  const TechnicalAnalysisRecentMonthPerformanceCount = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/technical_analysis_count",
    );
    verifyAuthorization(response);
    return response;
  };

  const getFAAdminData = async (companyName, quarter) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(SERVER_API_URL + "/api/admin/fa/get", {
      params: { company_name: companyName, quarter },
    });

    verifyAuthorization(response);
    return response;
  };

  const fetchcompnayresultDate = async (durationtype) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/admin/fa/fetch_company_result_date",
      { params: { durationtype } },
    );

    verifyAuthorization(response);
    return response;
  };

  const updateFAReport = async (id, report) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/admin/fa/update/${id}`,
      report,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const deleteFAReport = async (id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    console.log("jwt called");
    const response = await Axios.delete(
      `${SERVER_API_URL}/api/admin/fa/delete/${id}`,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const publishFAData = async ({ company_id, quarter }) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      SERVER_API_URL + "/api/admin/fa/publishQReport",
      {
        params: {
          company_id,
          quarter,
        },
      },
    );
    verifyAuthorization(response);
    return response;
  };

  const FACompanyList = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      `${SERVER_API_URL}/api/fa/fa_company_list`,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const GetFAAiPrompt = async ({ keyName, promptType, categoryId }) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

    const response = await Axios.get(`${SERVER_API_URL}/api/fa/get_ai_prompt`, {
      params: {
        keyName,
        promptType,
        categoryId,
      },
    });

    verifyAuthorization(response);
    return response.data;
  };

  const UpdateFAAiPrompt = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;

    const response = await Axios.post(
      `${SERVER_API_URL}/api/fa/update_ai_prompt`,
      payload,
    );

    verifyAuthorization(response);
    return response.data;
  };

  const GetPromptCategories = async () => {
    const response = await Axios.get(`${SERVER_API_URL}/api/fa/categories`);
    return response.data;
  };

  const AddPromptCategory = async (payload) => {
    const response = await Axios.post(
      `${SERVER_API_URL}/api/fa/categories/add`,
      payload,
    );
    return response.data;
  };

  const DeletePromptCategory = async (payload) => {
    const response = await Axios.post(
      `${SERVER_API_URL}/api/fa/categories/delete`,
      payload,
    );
    return response.data;
  };

  const UpdatePromptCategory = async (payload) => {
    const response = await Axios.post(
      `${SERVER_API_URL}/api/fa/categories/update`,
      payload,
    );
    return response.data;
  };

  const portfoliobuildersetting = async (userId, userIds) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/portfoliobuildersetting",
      { userId, userIds },
    );
    verifyAuthorization(response);
    return response;
  };

  const savePortfoliobuildersetting = async (userId, userIds, payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/portfoliobuildersetting/save",
      { userId, userIds, ...payload },
    );
    verifyAuthorization(response);
    return response;
  };

  const applyPortfolioBuilderChanges = async (
    userId,
    currentQuarter,
    weakTrend,
    adjuster,
    strongTrend,
  ) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/coreaudit/portfolioBuilderApply",
      { userId, currentQuarter, weakTrend, adjuster, strongTrend },
    );
    verifyAuthorization(response);
    return response;
  };

  const getTicketMetrics = async () => {
    const response = await Axios.post(
      `${SERVER_API_URL}/api/admin/getTicketMetrics`,
    );
    verifyAuthorization(response);
    return response.data;
  };

  /** TF Masters — shared voting/week/votes APIs (auth via Bearer token). */
  const tfmastersVotingSettings = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    let body =
      typeof payload === "object" && payload !== null && !Array.isArray(payload)
        ? payload
        : { userId: payload };

    if (
      body &&
      typeof body.userId === "object" &&
      body.userId !== null &&
      !Array.isArray(body.userId) &&
      (body.year === undefined || body.weekNumber === undefined)
    ) {
      body = body.userId;
    }

    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/votingSettings`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminVotingSettingsUpdate = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/votingSettings/update`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersWeekMembersList = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/weekMembers/list`,
      payload,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersVotesStats = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/votes/stats`,
      payload,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersVotesMine = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/votes/mine`,
      payload,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersVotesSync = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/votes/sync`,
      payload,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminDropdownEmails = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/dropdownEmails`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminUserDetails = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/userDetails`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminWeekMembersAdd = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/weekMembers/add`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminWeekMembersRemove = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/weekMembers/remove`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminWeekMembersUpdate = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/weekMembers/update`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminResultPublishStatus = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/resultPublish/status`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminResultPublish = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/resultPublish/publish`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminRecalculateWeekVoteTotals = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/weekMembers/recalculateVoteTotals`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminResetWeekVotes = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/weekMembers/resetVotes`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminResultUnpublish = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/resultPublish/unpublish`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersCommunityTopLatestWeek = async (payload) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/community/topLatestWeek`,
      payload,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const tfmastersAdminWeekMembersRewardsUpdate = async (body) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfmasters/admin/weekMembers/rewards/update`,
      body,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const fetchWellnessvideotime = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/fetchWellnessvideotime",
      { userId },
    );
    verifyAuthorization(response);
    return response;
  };
  const updateWellnessvideotime = async (userId, data) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      SERVER_API_URL + "/api/updateWellnessvideotime",
      { userId, ...data },
    );
    verifyAuthorization(response);
    return response;
  };

  const EventCardData = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/Event/EventCardData`,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const Ai_HelpDeckdata = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/ai_helpdeckdata`);
    verifyAuthorization(response);
    return response.data;
  };
  const Ai_queryresponse = async (id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/ai_queryresponse`,
      { id },
    );
    verifyAuthorization(response);
    return response.data;
  };
  const ai_response_update = async (data, user_id) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/ai_response_update`,
      { data, user_id },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const getCronDashboardData = async (date) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      `${SERVER_API_URL}/api/cron_dashboard/getcrondashboarddata`,
      {
        params: date ? { date } : {},
      },
    );
  
    verifyAuthorization(response);
    return response.data;
  };

   const MeetUpCreate = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/MeetUpCreate`,
        data || {},
      );
  
      verifyAuthorization(response);
      return response.data;
    };
    const Meetuprecordfetch = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupfetch`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetUpUpdate = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/MeetUpUpdate`,
        data || {},
      );
 
      verifyAuthorization(response);
      return response.data;
    };
    const MeetUpDelete = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/MeetUpDelete`,
        data || {},
      );
      
      verifyAuthorization(response);
      return response.data;
    };

    const MeetupCityFetch = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupCityFetch`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupCityGet = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupCityGet`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupCityAdd = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupCityAdd`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupCityUpdate = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupCityUpdate`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupCityDelete = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupCityDelete`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupVenueFetch = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupVenueFetch`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupVenueAdd = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupVenueAdd`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupVenueUpdate = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupVenueUpdate`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };
    const MeetupVenueDelete = async (data) => {
      const serviceToken = window.localStorage.getItem("serviceToken");
      Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
      const response = await Axios.post(
        `${SERVER_API_URL}/api/Admin/meetupVenueDelete`,
        data || {},
      );

      verifyAuthorization(response);
      return response.data;
    };

   // `params` optionally carries { wid } (or { year, week }) to load an earlier
    // TF Pulse week; omitted, the backend serves the current week as before.
    const fetchMyWatchlistRecord = async (user_id, params = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/mywatchlist/fetchmywatchlistrecored`,
      { user_id ,  ...params},
    );
    verifyAuthorization(response);
    return response.data;
  };

  const myWatchlistRecordAdd = async (fincode, userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/mywatchlist/myWatchlistrecoredadd`,
      { fincode, userId },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const myWatchlistRecordDelete = async (fincode, userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/mywatchlist/myWatchlistrecoreddelete`,
      { fincode, userId },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const fetchMyWatchlistCompanyData = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/mywatchlist/fetchcompany_data`,
      {},
    );
    verifyAuthorization(response);
    return response.data;
  };

   const tfpulseList = async (body = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/tfpulse/list`, body);
    verifyAuthorization(response);
    return response.data;
  };

  const tfpulseWeeks = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/tfpulse/weeks`, {});
    verifyAuthorization(response);
    return response.data;
  };

  const tfpulseUpdate = async (formData) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    const response = await Axios.post(
      `${SERVER_API_URL}/api/tfpulse/update`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${serviceToken}`,
        },
      },
    );
    verifyAuthorization(response);
    return response.data;
  };

  // ==============================|| RESEARCH REPORT ||============================== //

  const researchReportList = async (data = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/research-report/list`,
      data || {},
    );
    verifyAuthorization(response);
    return response.data;
  };

  const researchReportSsoGenerate = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/research/sso/generate`,
    );
    verifyAuthorization(response);
    return response.data;
  };

  const researchReportCompanies = async (data = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/research-report/companies`,
      data || {},
    );
    verifyAuthorization(response);
    return response.data;
  };

  const researchReportDelete = async (data = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/research-report/delete`,
      data || {},
    );
    verifyAuthorization(response);
    return response.data;
  };

  const researchReportCreate = async (formData) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    const response = await Axios.post(
      `${SERVER_API_URL}/api/research-report/create`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${serviceToken}`,
        },
      },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const researchReportUpdate = async (formData) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    const response = await Axios.post(
      `${SERVER_API_URL}/api/research-report/update`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${serviceToken}`,
        },
      },
    );
    verifyAuthorization(response);
    return response.data;
  };

  const fetchStockHistoricalData = async ({
    limit = 500,
    offset = 0,
    period = "10yr",
    filter = "default",
  } = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.get(
      `${SERVER_API_URL}/api/stock/historical-data`,
      {
        params: {
          limit,
          offset,
          period,
          filter,
        },
      },
    );
    verifyAuthorization(response);
    return response.data;
  };


  const fetchstagedata = async (stage = "S2", params = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/fetchstagedata`, {
      stage,
      ...params,
    });
    verifyAuthorization(response);
    return response.data;
  };

    const fetchsectorcompanies = async (params = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/fetchsectorcompanies`, {
      ...params,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const fetch_card_data = async (stage = "S2") => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/fetch_card_data`, {
      stage,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const fetch_stage_charts = async (stage = "S2") => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/fetch_stage_charts`, {
      stage,
    });
    verifyAuthorization(response);
    return response.data;
  };

  const filter_option = async () => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(`${SERVER_API_URL}/api/filter_option`, {});
    verifyAuthorization(response);
    return response.data;
  };


  const AiResponseUser_opinion = async(data)=>{
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/AiResponseUser_opinion`,
      data || {},
    );
    verifyAuthorization(response);
    return response.data;
  }

  
    const DisplayAIResponse = async (userId, userid, contactId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/DisplayAIResponse", {
      userId,
      userid,
       contact_id: contactId || null,
    });
    verifyAuthorization(response);
    return response;
  };
    const FetchBuliderMonth = async (userId) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(SERVER_API_URL + "/api/fetchbulidermonth", {
      userId
    });
    verifyAuthorization(response);
    return response;
  };

    // ==============================|| USER REPORT ||============================== //

  const userReportOverview = async (data = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/userReport/overview`,
      data || {},
    );
    verifyAuthorization(response);
    return response.data;
  };

  const userReportUsers = async (data = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/userReport/users`,
      data || {},
    );
    verifyAuthorization(response);
    return response.data;
  };

  // The CSV comes back as text rather than JSON, so it needs its own
  // responseType - the other two report calls can keep the default.
  const userReportExport = async (data = {}) => {
    const serviceToken = window.localStorage.getItem("serviceToken");
    Axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const response = await Axios.post(
      `${SERVER_API_URL}/api/userReport/users/export`,
      data || {},
      { responseType: "text" },
    );
    verifyAuthorization(response);
    return response.data;
  };
  return (
    <JWTContext.Provider
      value={{
        ...state,
        FetchBuliderMonth,
        DisplayAIResponse,
         AiResponseUser_opinion,
         fetch_card_data,
        fetch_stage_charts,
        filter_option,
        fetchstagedata,
        fetchsectorcompanies,
         MeetUpDelete,
                  MeetUpUpdate,
                  Meetuprecordfetch,
                  MeetUpCreate,
        MeetupCityFetch,
        MeetupCityGet,
        MeetupCityAdd,
        MeetupCityUpdate,
        MeetupCityDelete,
        MeetupVenueFetch,
        MeetupVenueAdd,
        MeetupVenueUpdate,
        MeetupVenueDelete,
        getCronDashboardData,
        ai_response_update,
        Ai_queryresponse,
        Ai_HelpDeckdata,
        fetchWellnessvideotime,
         updateWellnessvideotime,
        EventCardData,
        getTicketMetrics,
        portfoliobuildersetting,
        savePortfoliobuildersetting,
        applyPortfolioBuilderChanges,
        universitySubCategory,
        NewSubCategoryAdd,
        updateSubCategory,
        DeleteSubCategory,
        universityCategory,
        universityCoach,
        sumbitAddCoach,
        sumbitEditCoach,
        universityDelete,
        NewCategoryAdd,
        updateCategory,
        DeleteCategory,

        FetchDwonloadDataRewards,
        FetchDwonloadDataIdeaRewards,
        OtherHabitUserData,
        QuaterlyHabitUserData,
        WeeklyHabitUserData,
        DailyHabitUserData,
        SW_One_Percent_Download,
        TrendingSatelliteWatchlistDownload,
        TrendingCorePortfolioDownload,
        activeUserDetails,
        inactiveUserDetails,
        IdeaSatelliteSection,
        IdeaSatelliteUploadData,
        MarketHolidays,
        CompanyData,
        CompanyQuarterData,
        UserNotes,
        UpdateLink,
        UpdateNotes,
        AddUserNotes,
        Calculate2G3G,
        BulkUploaderWatchlist,
        ShortListCommentsAdd,
        SLWatchListInsert,
        SLWatchListDelete,
        EditSLWatchlist,
        SectorLeaderWatch,
        SectorLeaderShortList,
        ProfileImg,
        SW_One_Percent,
        sectorWatchlist,
        SectorInsightsData,
        SectorsList,
        InsertSectorwatchlist,
        Sectorwatchlistdata,
        DeleteSectorWatchlist,
        UserSectorRewards,
        LearningList,
        ResourceCategoryList,
        RefAdminGetCategories,
        RefAdminAddCategory,
        RefAdminUpdateCategory,
        RefAdminDeleteCategory,
        RefAdminBulkDeleteCategories,
        RefAdminGetResources,
        RefAdminAddResource,
        RefAdminUpdateResource,
        RefAdminDeleteResource,
        RefAdminBulkDeleteResources,
        RefAdminUploadResourceImage,
        DeleteBook,
        DeleteImage,
        DeleteFile,
        EditBook,
        UserIdeaReward,
        IdeaDashboard,
        RewardCategorys,
        NoteDateCorewatichlist,
        loginRewards,
        ApiData,
        RewardsPointGet_user,
        DeleteVideoLink,
        UpdateVideo_Link,
        VideoLinkDatas,
        VideolinkAddCoreWatichList,
        PdfLinkDatas,
        PdflinkAddCoreWatichList,
        DeletePdfLink,
        UpdatePdf_Link,
        GetLoginDemoVideoLink,
        GetDemoVideoLink,
        AddDeomvideolink,
        EditDemoVideoLink,
        DeletVideoLink,
        OnboardingCheckList_Delete_OR_Edit,
        OnboardingCheckList_Added,
        corewatchlisthistoryAdmin,
        DownLoadCoreWhatchList,
        EditcoreWatchlist,
        AddCompanyflag,
        ColorFlagsSet,
        PresentedByName,
        stockalertviewdata,
        StockAlertList,
        stockalertUpdate,
        stockalertDelete,
        DeleteExpired,
        stockInsert,
        stocklist,
        Displayresponse,
        UpdateResponse,
        GetcategorylistTool,
        GetdataHelpManagement,
        updateCoreAudit,
        ProcessUpdate,
        CheckStatusOfLearning,
        DeleteLearningHistory,
        InsertLearningHistory,
        Alloccupationlist,
        CheckTotalCountOfLearning,
        GetLearing,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
        verifyotp,
        newPassword,
        Profiledata,
        registerOtpVerify,
        SociallinkView,
        updateSociallink,
        AllUsers,
        EventReport,
        Addfriend,
        Removefriend,
        MyFollower,
        MyFriends,
        MyGrouplist,
        Statistics,
        MemberProfile,
        charitylist,
        watchlist,
        charitylistdata,
        deletecharitydata,
        Insertcharitylist,
        Insertwatchlist,
        weekdata1,
        weekly,
        watchlistdata,
        vivekwatchlistdata,
        weeklist,
        DeleteWatchlist,
        Coreauditcompanylist,
        Coreauditdatalist,
        insertCoreAudit,
        deleteCoreAudit,
        copyVivekAudit,
        Editcharitydata,
        Resourceslist,
        ResearchList,
        TemplateList,
        TechnoList,
        ApplicationsList,
        checkanswerdata,
        Qscore,
        Email,
        Rewards_chart,
        TopTwentyuserDetailsBaseOnRewaerdPoint,
        TrendingCorePortfolio,
        TrendingSatelliteWatchlist,
        RewardsPoint,
        habits,
        habitchart,
        CheckStatusOfHabit,
        GetHabitsNamelist,
        InsertHabit,
        Pastevent,
        Futurevent,
        Todayevent,
        GetAttendance,
        InsertAttendance,
        updateVideoLink,
        CalendarEventsList,
        AttendanceChartData,
        profilevisibilityInsert,
        profilevisibility,
        UserDetails,
        Editdatauser,
        EditSavedata,
        Adddatauser,
        PanXlsxUpdate,
        getEc2TypeChangeToggle,
        updateEc2TypeChangeToggle,
        Csvreport,
        CharityReport,
        Watchlist,
        CoreAuditReportdata,
        LoginReport,
        NotloginReportdata,
        updateEvent,
        DeleteSchedule,
        GetEventsList,
        AddEvent,
        AlertReport,
        GroupnameList,
        EventList,
        GetcategoryWithoutLogin,
        ContactInsertWithoutLogin,
        Getcategory,
        ContactInsert,
        corewatchlist,
        coreInsertwatchlist,
        corewatchlisthistory,
        DeleteCoreWatchlist,
        EditCoreWatchlistgetdata,
        GetHabitsList,
        Gethabitsdata,
        MonthlyLineChart,
        handleChangedeletedata,
        CatcusDeleteCoreWatchlist,
        Catcuswatchlisthistory,
        GetAllCompanyforStockAlert,
        CreateStockalert,
        GetStockalertCount,
        GetStockalertList,
        StockAlertDelete,
        UpdateStockAlert,
        Gamefications,
        getMyList,
        getMyListFilters,
        getWeeklyFlagsList,
        getWeeklyFlagsMeta,
        searchWeeklyFlagCompanies,
        getWeeklyFlagCompanyWeeks,
        saveWeeklyFlag,
        updateWeeklyFlag,
        deleteWeeklyFlag,
        bulkUpdateWeeklyFlags,
        bulkDeleteWeeklyFlags,
        uploadWeeklyFlagsCsv,
        Onboarding,
        OnboardingPopupStatus,
        OnboardingPopupDontShowAgain,
        DiamondPartnerProgram,
        GetCalculatorSettings,
        SaveCalculatorSettings,
        CoreauditCashdatalist,
        UpdateCoreauditCashdata,
        DeleteAllSchedule,
        UserDetailsQic,
        MemberDetails,
        TeamdetailsdataQic,
        EditdatauserQic,
        TeamEditDetailsData,
        RegisterCheck,
        EditTeamQic,
        EditSavedataQic,
        EditsavedTeamQic,
        SaveCreateTeam,
        SaveRenameTeam,
        UserTeamDetails,
        QicEventDetails,
        EventName,
        QicEventDetailsSave,
        SaveCreateEvent,
        DocumentdetailsdataQic,
        EditDocQic,
        EditsaveddocQic,
        StageShortlistTeamDetails,
        StageAttendDetails,
        ShortlistStatusNextRound,
        QicUploadData,
        QICSemifinalist,
        QicStageDetailsSave,
        EditStageData,
        EditActiveEvent,
        SendMessageToAdmins,
        DeleteSelectedData,
        AddedQicUser,
        BusinessRanking_Quiz_Questions,
        BusinessRanking_Quiz_Questions_Answer,
        BusinessRanking_Quiz_Questions_Answer_Check,
        Strong_Quiz_Questions,
        Strong_Quiz_Questions_Answer,
        Strong_Quiz_Questions_Answer_Check,
        Voting_Insert,
        get10Y,
        QicVotingDetails,
        EditVotingQic,
        QicVotingDetailsSave,
        QicVotingDetailsSaveFinal,
        AllVotePoints,
        DocumentdetailsdataQicWinner,
        EditDocQicWinner,
        EditsaveddocQicWinner,
        SectorIdeasComment,
        getuserslist,
        AddContributors,
        showCompanyList,
        showContribtorData,
        deleteContribtorData,
        updateContributor,
        showNotesData,
        publishNote,
        updateNoteHistory,
        UpdateAbout,
        CompanyList,
        AddCompany,
        DeleteCompany,
        updatemonthstatus,
        // coreIdeascuesupdate,
        coreIdeastrendscoreupdate,
        Updateideacoretable,
        Top30IdeaCore,
        IdeaSatellite,
        editAccess,
        PriceMarker,
        PriceMarkerSector,
        CMQuarterData,
        CMQuarterDataInsert,
        CMQuarterDataDelete,
        HallOfFame,
        IdeaSectorWc,
        IdeaSatelliteOneWc,

        WellnessEventGetData,
        WellnessEventAdd,
        WellnessEventEdit,
        WellnessEventDelete,
        WellnessEventData,
        WellnessDailyEventData,
        QIC_popup_status,
        BookClubEvent,
        BooksList,
        DeleteBookData,
        DeleteBookImage,
        UpdateBookData,
        BookData,
        CoreauditdatalistAnalysis,
        getcoreauditMarketCapAnalysis,
        FetchMaxRewardPoints,
        coreThemeAnalysis,
        coreCapAnalysis,
        coreWLAnalysis,
        alertThemeAnalysis,
        alertCapAnalysis,
        userAI,
        IdeaDashboardYearToppers,
        IdeaDashboardAnnualToppers,
        UtilityReportTableData,
        ModuleTraceInit,
        GetIdeasCoreList,
        GetIdeasCoreList_FA,
        GetTrendReason,
        GetCompanyDescReport,
        GetCompanyEarningsReport,
        UploadEarningsReport,
        GetAllCompanyList,
        GetFAPerformanceCount,
        TechnicalAnalysisRecentMonthPerformanceCount,
        getFAAdminData,
        fetchcompnayresultDate,
        deleteFAReport,
        updateFAReport,
        publishFAData,
        FACompanyList,
        GetFAAiPrompt,
        UpdateFAAiPrompt,
        googleLogin,
        GetPromptCategories,
        AddPromptCategory,
        DeletePromptCategory,
        UpdatePromptCategory,
        tfmastersAdminDropdownEmails,
        tfmastersAdminUserDetails,
        tfmastersAdminVotingSettingsUpdate,
        tfmastersAdminWeekMembersAdd,
        tfmastersAdminWeekMembersRemove,
        tfmastersAdminWeekMembersUpdate,
        tfmastersAdminResultPublish,
        tfmastersAdminResultPublishStatus,
        tfmastersVotesMine,
        tfmastersVotesStats,
        tfmastersVotesSync,
        tfmastersVotingSettings,
        tfmastersWeekMembersList,
        tfmastersAdminRecalculateWeekVoteTotals,
        tfmastersAdminResetWeekVotes,
        tfmastersAdminResultUnpublish,
        tfmastersCommunityTopLatestWeek,
        tfmastersAdminWeekMembersRewardsUpdate,
        FeatureRequestsGetList,
        FeatureRequestsGetDevelopers,
        FeatureRequestsGetTesters,
        FeatureRequestsGetQueryTypes,
        FeatureRequestsGetEnvStatuses,
        FeatureRequestsGetQueryDetail,
        FeatureRequestsUpdate,
        FeatureRequestsDelete,
        onboardingAdminGetStages,
        onboardingAdminSaveStage,
        onboardingAdminDeleteStage,
        onboardingAdminGetItems,
        onboardingAdminSaveItem,
        onboardingAdminDeleteItem,
        onboardingAdminSetStatus,
        onboardingAdminReorder,
        fetchMyWatchlistRecord,
        myWatchlistRecordAdd,
        myWatchlistRecordDelete,
        fetchMyWatchlistCompanyData,
        fetchStockHistoricalData,
        tfpulseList,
        tfpulseWeeks,
        tfpulseUpdate,
        researchReportList,
        researchReportSsoGenerate,
        researchReportCompanies,
        researchReportCreate,
        researchReportUpdate,
        researchReportDelete,
        userReportOverview,
        userReportUsers,
        userReportExport,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node,
};

export default JWTContext;
